/**
 * SubmitStep — F-003.4 Phase 4 (IMPL-P4-005) + F-005 Phase 5 (IMPL-P5-005).
 *
 * Ultimo step del wizard:
 *  - mode='create' (default): PDFDropzone (full) + PDFPreview + ResponsibilityCheckbox
 *    (full) + bottone "Invia pratica" (chiama `submit(praticaId, ...)`).
 *  - mode='correct' (F-005): consent fresco unchecked + bottone "Invia correzione"
 *    (chiama `submitCorrezione`). PDF replace è opzionale (UC-014 Extension 6b)
 *    e non bloccante: se l'operatore ha solo segnalato dati strutturati il
 *    cliente può ri-inviare senza ri-uploadare il PDF.
 *
 * Architettura interna:
 *  - `SubmitStep` dispatcha al `CreateSubmitStep` (legacy F-003.4) o al
 *    `CorrectSubmitStep` (F-005) in base a `mode`. Splittare i due flussi
 *    evita di chiamare hook (useNavigate, useQueryClient, useFormContext)
 *    nel flusso di creazione che storicamente non li richiedeva — i test
 *    esistenti renderizzano `SubmitStep` senza Router/QueryClientProvider.
 *
 * Pattern (vedi ContractDenseForm):
 *  - Bottone usa `aria-disabled` (non `disabled` nativo) così screen reader
 *    può annunciarlo come disabilitato senza renderlo unfocusable.
 *  - `aria-describedby` punta a un live `sr-only` che spiega cosa manca.
 *  - Hash del consent text computato una volta sola al mount via
 *    `sha256Hex` (Web Crypto API). Il backend ri-computa lo stesso hash
 *    server-side e lo persiste nell'audit log.
 *
 * F-005 race handling (HTTP 409):
 *  - L'API `POST /api/v1/pratiche/:id/correzione` può rispondere 409 se la
 *    pratica è stata aggiornata altrove (es. operatore ha sbloccato).
 *  - In tal caso mostriamo un Alert dedicato, invalidiamo la query
 *    `pratiche-cliente/detail` e navighiamo alla pagina detail.
 *
 * Nota: il progetto non espone (ancora) un hook `useToast`, quindi gli stati
 * di successo/errore sono mostrati inline tramite `<Alert>`. Una volta
 * introdotto un toast system possiamo migrare senza cambiare contratto.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { sha256Hex } from '@/lib/sha256';
import { PDFDropzone } from '../PDFDropzone';
import { PDFPreview } from '../PDFPreview';
import { ResponsibilityCheckbox } from '../ResponsibilityCheckbox';
import {
  submit,
  getPraticaPrezzo,
  isPaymentRequired,
  type PdfConfirmResponse,
  type PraticaView,
} from '../../lib/pratica-client';
import { resolveSubmitError, type SubmitErrorView } from '../../lib/submit-error';
import { CONSENT_TEXT } from '../../lib/consent';
import { submitCorrezione } from '../../../pratiche-cliente/lib/cliente-pratiche-client';
import type { ContractDraft } from '@app/shared';
import type { ContractFormMode } from '../../hooks/useContractForm';

const CORRECT_CONSENT_LABEL =
  'Confermo che i dati corretti sono veritieri e completi.';

export interface SubmitStepProps {
  praticaId: string;
  /** F-005: 'correct' attiva il flusso di correzione (UC-014). Default 'create'. */
  mode?: ContractFormMode;
  /** F-010: true per i privati → mostra prezzo e "Paga e invia €X". */
  paymentMode?: boolean;
  /**
   * F-004 (IMPL-P4-002 fix TR-001): gate opzionale eseguito PRIMA del submit
   * reale nel flusso `create`. `handleSubmit` lo `await`-a; se ritorna `false`
   * (o una Promise che risolve `false`) il submit viene ANNULLATO. Usato per
   * interporre una conferma esplicita (es. `CessioneConfirmDialog` per la
   * CESSIONE, WCAG 3.3.4). Additivo: se non passato, il comportamento
   * F-003/F-009/F-010 è invariato (nessun gate).
   */
  onBeforeSubmit?: () => boolean | Promise<boolean>;
  /**
   * F-004 (IMPL-P4-002 fix TR-002): notifica al caller il cambio dello stato
   * "submit in corso" del flusso `create`, così un dialog di conferma esterno
   * può riflettere il pending reale (disabilita azioni, blocca la chiusura).
   * Additivo — no-op se non passato.
   */
  onSubmittingChange?: (submitting: boolean) => void;
  onSubmitted: (pratica: PraticaView) => void;
}

export function SubmitStep({
  praticaId,
  mode = 'create',
  paymentMode = false,
  onBeforeSubmit,
  onSubmittingChange,
  onSubmitted,
}: SubmitStepProps) {
  if (mode === 'correct') {
    return <CorrectSubmitStep praticaId={praticaId} onSubmitted={onSubmitted} />;
  }
  return (
    <CreateSubmitStep
      praticaId={praticaId}
      paymentMode={paymentMode}
      onBeforeSubmit={onBeforeSubmit}
      onSubmittingChange={onSubmittingChange}
      onSubmitted={onSubmitted}
    />
  );
}

/** Formatta cents (stringa) in valuta IT, es. "3500" → "€ 35,00". */
function formatEuroFromCents(cents: string): string {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return (n / 100).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
}

/**
 * Flusso storico F-003.4: PDF upload + consent + submit.
 * Non chiama hook router/query — i test esistenti lo renderizzano "nudo".
 */
function CreateSubmitStep({
  praticaId,
  paymentMode = false,
  onBeforeSubmit,
  onSubmittingChange,
  onSubmitted,
}: {
  praticaId: string;
  paymentMode?: boolean;
  onBeforeSubmit?: () => boolean | Promise<boolean>;
  onSubmittingChange?: (submitting: boolean) => void;
  onSubmitted: (pratica: PraticaView) => void;
}) {
  const [pdfInfo, setPdfInfo] = useState<PdfConfirmResponse | null>(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentTextHash, setConsentTextHash] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // F-004 (TR-002): riflette il pending reale al caller (es. per disabilitare
  // le azioni di un dialog di conferma esterno). No-op se il callback è assente.
  useEffect(() => {
    onSubmittingChange?.(submitting);
  }, [submitting, onSubmittingChange]);
  const [prezzoCents, setPrezzoCents] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string; action?: SubmitErrorView['action'] }
    | null
  >(null);

  // Compute consent text hash once on mount (CONSENT_TEXT is a module constant).
  useEffect(() => {
    let cancelled = false;
    sha256Hex(CONSENT_TEXT).then((hash) => {
      if (!cancelled) setConsentTextHash(hash);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // F-010: in payment mode carica il prezzo privati per la CTA (best-effort).
  useEffect(() => {
    if (!paymentMode) return;
    let cancelled = false;
    getPraticaPrezzo(praticaId)
      .then((r) => {
        if (!cancelled) setPrezzoCents(r.prezzoCents);
      })
      .catch(() => {
        /* prezzo non disponibile → CTA generica, il submit gestirà l'errore */
      });
    return () => {
      cancelled = true;
    };
  }, [paymentMode, praticaId]);

  const canSubmit = !!pdfInfo && consentGiven && !!consentTextHash && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    // F-004 (TR-001): gate esplicito PRIMA del submit reale. Se il gate ritorna
    // `false` (es. la conferma cessione non è ancora stata data — apre il dialog
    // e attende), il submit viene annullato qui SENZA entrare in stato
    // "submitting", così il click che poi conferma può re-invocare handleSubmit.
    if (onBeforeSubmit) {
      const proceed = await onBeforeSubmit();
      if (!proceed) return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = await submit(praticaId, {
        consentGiven: true,
        consentTextHash,
      });
      // F-010: privati → redirect al Checkout Stripe (resta in "submitting").
      if (isPaymentRequired(result)) {
        window.location.href = result.checkoutUrl;
        return;
      }
      setFeedback({
        kind: 'success',
        message: `Pratica inviata con successo (ID: ${result.id}).`,
      });
      onSubmitted(result);
    } catch (err) {
      const view = resolveSubmitError(err);
      setFeedback({ kind: 'error', message: view.message, action: view.action });
    } finally {
      // In caso di redirect non resettiamo (la pagina sta cambiando); qui
      // riabilitiamo solo se siamo ancora montati (success/errore non-redirect).
      setSubmitting(false);
    }
  }

  const prezzoLabel = prezzoCents ? formatEuroFromCents(prezzoCents) : null;
  const submitLabel = submitting
    ? paymentMode
      ? 'Reindirizzamento al pagamento…'
      : 'Invio in corso...'
    : paymentMode
      ? prezzoLabel
        ? `Paga e invia ${prezzoLabel}`
        : 'Paga e invia'
      : 'Invia pratica';

  return (
    <div className="space-y-6" data-testid="submit-step">
      <div>
        <h3 className="text-lg font-semibold mb-1">Carica il contratto PDF</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Carica il contratto firmato in formato PDF (max 10 MB).
        </p>
        <PDFDropzone praticaId={praticaId} onUploaded={setPdfInfo} variant="full" />
      </div>

      {pdfInfo && (
        <div>
          <h3 className="text-sm font-medium mb-2">Anteprima contratto</h3>
          <PDFPreview
            previewUrl={`/api/v1/pratiche/${praticaId}/pdf/preview`}
            fileName="contratto.pdf"
          />
        </div>
      )}

      <ResponsibilityCheckbox
        variant="full"
        checked={consentGiven}
        onCheckedChange={setConsentGiven}
        id="submit-step-consent"
      />

      {/* Live region descrittiva: spiega allo screen reader perché il bottone
          è disabilitato. Visibile solo ad AT (sr-only). */}
      <div id="submit-step-help" className="sr-only" aria-live="polite">
        {!pdfInfo && 'Carica il PDF prima di procedere. '}
        {!consentGiven && 'Spunta la casella di responsabilità per procedere.'}
      </div>

      {feedback && (
        <Alert
          role={feedback.kind === 'error' ? 'alert' : 'status'}
          aria-live={feedback.kind === 'error' ? 'assertive' : 'polite'}
          variant={feedback.kind === 'error' ? 'destructive' : undefined}
        >
          {feedback.message}
          {feedback.kind === 'error' && feedback.action && (
            <>
              {' '}
              <a
                href={feedback.action.href}
                className="font-medium underline underline-offset-2"
              >
                {feedback.action.label}
              </a>
            </>
          )}
        </Alert>
      )}

      {/* F-010: anteprima prezzo per i privati (pay-per-practice). */}
      {paymentMode && prezzoLabel && (
        <div
          className="rounded-md border bg-muted/30 p-3 text-sm"
          data-testid="price-preview"
        >
          <span className="text-muted-foreground">Servizio di registrazione</span>{' '}
          <span className="font-semibold">{prezzoLabel}</span>{' '}
          <span className="text-muted-foreground">(IVA inclusa)</span>
        </div>
      )}

      <Button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        aria-disabled={!canSubmit}
        aria-describedby={!canSubmit ? 'submit-step-help' : undefined}
        className={cn('w-full', !canSubmit && 'opacity-50 cursor-not-allowed')}
      >
        {submitLabel}
      </Button>
    </div>
  );
}

/**
 * F-005 correction flow: consent fresco + submitCorrezione + 409 race handling.
 * Richiede MemoryRouter + QueryClientProvider + FormProvider (parent wizard).
 */
function CorrectSubmitStep({
  praticaId,
  onSubmitted: _onSubmitted,
}: {
  praticaId: string;
  onSubmitted: (pratica: PraticaView) => void;
}) {
  const form = useFormContext<ContractDraft>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pdfInfo, setPdfInfo] = useState<PdfConfirmResponse | null>(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string }
    | null
  >(null);

  const canSubmit = consentGiven && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const draft = form.getValues() as unknown as Record<string, unknown>;
      const pdfS3Key = pdfInfo?.pdfS3Key;
      await submitCorrezione(praticaId, {
        datiStrutturati: draft,
        consent: true,
        pdfS3Key,
      });
      setFeedback({
        kind: 'success',
        message: 'Correzione inviata con successo.',
      });
      navigate(`/app/pratiche/${praticaId}`);
    } catch (err) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr.status === 409) {
        setFeedback({
          kind: 'error',
          message:
            'La pratica è stata aggiornata altrove. Ricarica e riprova.',
        });
        queryClient.invalidateQueries({
          queryKey: ['pratiche-cliente', 'detail', praticaId],
        });
        navigate(`/app/pratiche/${praticaId}`);
      } else {
        setFeedback({
          kind: 'error',
          message:
            apiErr.message ?? "Errore durante l'invio della correzione.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="submit-step">
      <div>
        <h3 className="text-lg font-semibold mb-1">
          Sostituisci il contratto PDF (opzionale)
        </h3>
        <p className="text-sm text-muted-foreground mb-3">
          Carica un nuovo PDF solo se l&apos;operatore ha segnalato il documento
          come mancante o illeggibile.
        </p>
        <PDFDropzone praticaId={praticaId} onUploaded={setPdfInfo} variant="full" />
      </div>

      {pdfInfo && (
        <div>
          <h3 className="text-sm font-medium mb-2">Anteprima contratto</h3>
          <PDFPreview
            previewUrl={`/api/v1/pratiche/${praticaId}/pdf/preview`}
            fileName="contratto.pdf"
          />
        </div>
      )}

      <div className="flex items-start gap-3">
        <Checkbox
          id="submit-step-correct-consent"
          checked={consentGiven}
          onCheckedChange={(v) => setConsentGiven(v === true)}
          aria-label={CORRECT_CONSENT_LABEL}
        />
        <label
          htmlFor="submit-step-correct-consent"
          className="text-sm leading-relaxed cursor-pointer"
        >
          {CORRECT_CONSENT_LABEL}
        </label>
      </div>

      <div id="submit-step-help" className="sr-only" aria-live="polite">
        {!consentGiven && 'Spunta la casella di responsabilità per procedere.'}
      </div>

      {feedback && (
        <Alert
          role={feedback.kind === 'error' ? 'alert' : 'status'}
          aria-live={feedback.kind === 'error' ? 'assertive' : 'polite'}
          variant={feedback.kind === 'error' ? 'destructive' : undefined}
        >
          {feedback.message}
        </Alert>
      )}

      <Button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || !consentGiven}
        aria-disabled={!canSubmit}
        aria-describedby={!canSubmit ? 'submit-step-help' : undefined}
        className={cn('w-full', !canSubmit && 'opacity-50 cursor-not-allowed')}
      >
        {submitting ? 'Invio correzione…' : 'Invia correzione'}
      </Button>
    </div>
  );
}
